import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid2";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from '@mui/material/styles';
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LicenceCheck from "../../Authorisation/LicenceCheck";
import FarTooBigChart from "../../Charts/TripRate/FarTooBigTripRateBarChartWithAFarTooBigName";
import ModalTypeChart from "../../Charts/TripRate/ModalTypeChart";
import TripRateSitesGraphDialog from "../../Charts/TripRate/TripRateSitesGraphDialog";
import { TripRateSitesGraphDirection, TripRateSitesGraphView } from "../../Charts/TripRate/TripRateSitesGraphTypes";
import PeakHourChart from "../../Charts/TripRate/PeakHourChart";
import TripRateTotalChart from "../../Charts/TripRate/TripRateBarChartTotalVehicles";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import { SurveyViewModel } from "../../Contexts/SessionState";
import AdditionalInfo from "../../Popups/AdditionalInfo";
import PDFLink from "../../Popups/PDFLink";
import WarningPopup from "../../Popups/WarningPopup";
import Line from "../../Utilities/Line";
import ExportViewModel from "../ViewModels/ExportViewModel";
import ModeSelectionViewModel from "../ViewModels/ModeSelectionViewModel";
import TripRateCalculationFiltersViewModel from "../ViewModels/TripRateCalculationFiltersViewModel";
import TripRateCalculationViewModel from "../ViewModels/TripRateCalculationViewModel";
import TripRateDay from "../ViewModels/TripRateDay";
import TripRateExportViewModel from "../ViewModels/TripRateExportViewModel";
import CrossTestDialog from "./CrossTestDialog";
import DeselectedSurveysViewModel from "./DeselectedSurveysViewModel";
import ExportDialog from "./ExportDialog";
import KeyPeriodDialog from "./KeyPeriodDialog";
import TripRateTable from "./TripRateTable";
import { useTripRateContext } from "../TripRateContext";
import SurveyPeriod from "./SurveyPeriod";
import { getFilteredSurveyRefs, getFilteredSurveys } from "../../TripRateNew/TriprateFilters";
import { getTripRateCalculationSurveyRefs } from "../TripRateSurveyRefs";
import formatDate from '../../Utilities/FormateDayjsToDateOnly';
import dayjs from "dayjs";

export default function TripRateCalculationPage() {
    const theme = useTheme();
    const { post, postPDF, postCSV } = useContext(APIContext);
    const [tripRate, setTripRate] = useState<TripRateCalculationViewModel | null>(null);
    const [chart, setChart] = useState<TripRateCalculationViewModel[]>([]);
    const [extrapolateValue, setExtrapolateValue] = useState<string>("1");
    const [extrapolate, setExtrapolate] = useState(false);
    const [hourly, setHourly] = useState(true);
    const [vehicleType, setVehicleType] = useState(0);
    const { sessionState } = useContext(SessionContext);
    const [crossTestOpen, setCrossTestOpen] = useState(false);
    const [peakHourOpen, setPeakHourOpen] = useState(false);
    const [peakHourChartOpen, setPeakChartHourOpen] = useState(false);
    const [openTripRateGraph, setOpenTripRateGraph] = useState(false);
    const [openModeChart, setOpenModeChart] = useState(false);
    const [modes, setModes] = useState<ModeSelectionViewModel[]>([]);
    const [exportDialog, setExportDialog] = useState(false);
    const [openMultiModal, setOpenMultiModal] = useState(false);
    const [startSlice, setStartSlice] = useState(0);
    const [endSlice, setEndSlice] = useState(23);
    const [siteGraphOpen, setSiteGraphOpen] = useState(false);
    const [siteGraphView, setSiteGraphView] = useState<TripRateSitesGraphView>("selection");
    const [siteGraphMode, setSiteGraphMode] = useState<number | null>(null);
    const [siteGraphDirection, setSiteGraphDirection] = useState<TripRateSitesGraphDirection>("Total");
    const { surveys } = useTripRateContext();
    const [loading, setLoading] = useState(true);


    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const fromTab = params.get('fromTab');
    const selectedModeParam = params.get('selectedMode');
    const selectedParam = params.get('selectedParam');
    const effectiveHeaderIndex = selectedParam != null
        ? Number.parseInt(selectedParam)
        : sessionState.primaryFilters.headerIndex;
    const extrapolateValueNumeric = extrapolateValue === "" ? 0 : Number(extrapolateValue) || 0;
    const tripRateBasis = [
        tripRate?.calculationValue,
        tripRate?.unit?.includes("*") ? null : tripRate?.unit,
        tripRate?.abbreviation !== tripRate?.unit ? tripRate?.abbreviation : null
    ].filter(Boolean).join(" ");


    const filteredSurveyIds = useMemo(() => {
        return getFilteredSurveyRefs(surveys);
    }, [surveys]);

    const filteredSurveys = useMemo(() => {
        return getFilteredSurveys(surveys);
    }, [surveys]);


    useEffect(() => {
        if (tripRate && tripRate.totals.length > 0) {
            sessionState.tripRateAvailableTimeRange = getFormattedPeriodRange(tripRate.totals);
            setExtrapolateValue(String(tripRate?.calculationValue ?? 1));
        }
    }, [tripRate]);

    const tripRateSurveyRefs = useMemo(
        () => getTripRateCalculationSurveyRefs(location.search, sessionState.selectedSitesAndSurveys, filteredSurveyIds),
        [location.search, sessionState.selectedSitesAndSurveys, filteredSurveyIds]
    );

    useEffect(() => {
        if (filteredSurveys.length === 0) return;
        setExtrapolateValue(String(tripRate?.calculationValue ?? 1));
        getData();
    }, [vehicleType, filteredSurveys, tripRateSurveyRefs]);

    useEffect(() => {
        if (filteredSurveys.length === 0) return;
        if (selectedModeParam != null) {
            setVehicleType(Number.parseInt(selectedModeParam));
        }
        const atc = sessionState.primaryFilters.surveyTypes.includes(2) && filteredSurveys.filter(s => s.isATC).length > 0;
        const servicing = sessionState.primaryFilters.serviceVehicles.includes("Y");
        const multimodal = sessionState.landusePage.isMultiModal;
        const earliestDate = filteredSurveys.reduce((min, current) => {
            if (min.rawDate > current.rawDate) {
                return current;
            }
            else {
                return min;
            }
        });
        post(
            `TripRateCalculation`,
            `GetModes?atc=${atc}&servicing=${servicing}&multimodal=${multimodal}&startdate=${formatDate(earliestDate.rawDate)}`,
            tripRateSurveyRefs,
            setModes
        );
    }, [filteredSurveys, selectedModeParam, tripRateSurveyRefs])

    const getLargeChartData = () => {
        const vm = {
            ids: tripRateSurveyRefs,
            surveyRectTypes: [0, 3, 2, 1, 11, 12, 13, 14]
        }
        post('TripRateCalculation', `GetTripRateCalculations?headerIndex=${sessionState.primaryFilters.headerIndex}&landUse=${sessionState.tripRateMain}&subUse=${sessionState.tripRateSub}&hourly=${hourly}&startTime=${startSlice}&endTime=${endSlice}`, vm, setChart);
    };


    const openSiteTripRateGraph = () => {
        setSiteGraphOpen(true);
        setSiteGraphView("selection");
        setSiteGraphMode(null);
        setSiteGraphDirection("Total");
    };

    const closeSiteTripRateGraph = () => {
        setSiteGraphOpen(false);
    };

    const getFormattedPeriodRange = (data: TripRateDay[]) => {
        const validPeriods = data.filter(day => day.tripRate !== null && day.tripRate !== 0);
        if (validPeriods.length === 0) return "N/A";

        const firstStartTime = validPeriods[0].period?.split("-")[0];
        const lastEndTime = validPeriods[validPeriods.length - 1].period?.split("-")[1];

        return `${firstStartTime} - ${lastEndTime}`;
    };

    const getData = (newHourly?: boolean, newStartSlice?, newEndSlice?) => {
        setLoading(true);
        var surveyList: string[] = tripRateSurveyRefs;
        post('TripRateCalculation', `GetTripRateCalculation?headerIndex=${effectiveHeaderIndex}&landUse=${sessionState.tripRateMain}&subUse=${sessionState.tripRateSub}&vehicleType=${vehicleType}&hourly=${newHourly ?? hourly}&startTime=${newStartSlice ?? startSlice}&endTime=${newEndSlice ?? endSlice}`, surveyList, (data) => {
            setTripRate(data);
            setLoading(false);
        });
    };

    //TODO: Remove this function when the Trip Rate Graph PDF button is removed
    // const exportTripRateChartPdf = () => {
    //     var ids: string[] = tripRateSurveyRefs;
    //     postPDF('Chart', `GetTripRateChartCSV?headerIndex=${sessionState.primaryFilters.headerIndex}&direction=Totals`, "TripRateGraph", false, ids);
    // }

    const getReport = (exportFilters: ExportViewModel, auditCode?: string) => {
        exportFilters.exportType === "csv" ? getReportCSV(exportFilters, auditCode) : getReportPDF(exportFilters, auditCode)
    }

    function normalizeDate(input: string | Date | dayjs.Dayjs | null | undefined): Date | null {
        if (!input) return null;

        if (dayjs.isDayjs(input)) {
            return input.toDate(); // convert Dayjs → Date
        }

        if (typeof input === "string" || typeof input === "number") {
            return new Date(input); // parse ISO strings
        }

        if (input instanceof Date) {
            return input; // already a Date
        }

        return null;
    }

    const getReportCSV = (exportFilters: ExportViewModel, auditCode?: string) => {
        const data = new TripRateExportViewModel()
        const exportFiltersVm = {
            ...exportFilters,
            explanatoryCommentsIncluded: !!exportFilters.explanatoryCommentsIncluded,
            auditCode: auditCode ?? ''
        };
        var filter: TripRateCalculationFiltersViewModel = {
            tripRateMain: sessionState.tripRateMain,
            tripRateSub: sessionState.tripRateSub,
            // Date selection is owned by the land-use page.  Copy it into the
            // legacy primary-filters payload because the CSV exporter reads dates
            // from there when producing the filtering summary.
            primaryFilters: {
                ...sessionState.primaryFilters,
                startDate: normalizeDate(sessionState.landusePage.startDate),
                endDate: normalizeDate(sessionState.landusePage.endDate),
            },
            secondaryFilters: sessionState.secondaryFilters,
            landUsePage: sessionState.landusePage,
        }

        data.startTime = startSlice;
        data.endTime = endSlice;
        data.filtersVm = filter;
        data.autoRemovedSurveysCountList = 0;
        data.manuallyRemovedSurveysCount = 0;
        data.selectedSurveys = tripRateSurveyRefs;
        data.exportFiltersVm = exportFiltersVm;
        data.hourly = hourly
        data.extrapolate = extrapolate;
        data.extrapolateValue = extrapolateValueNumeric;
        data.SelectedVehicleType = vehicleType;
        const reportTitle = data.exportFiltersVm.reportTitle?.trim();
        postCSV("CsvGenerator", `GetTripRateCSV?unit=${tripRate?.unit}`, reportTitle ? reportTitle : "Trip Rate", data);
    }

    const getReportPDF = (exportFilters: ExportViewModel, auditCode?: string) => {
        const data = new TripRateExportViewModel()
        const exportFiltersVm = {
            ...exportFilters,
            explanatoryCommentsIncluded: !!exportFilters.explanatoryCommentsIncluded,
            auditCode: auditCode ?? ''
        };
        var filter: TripRateCalculationFiltersViewModel = {
            tripRateMain: sessionState.tripRateMain,
            tripRateSub: sessionState.tripRateSub,
            primaryFilters: sessionState.primaryFilters,
            secondaryFilters: sessionState.secondaryFilters,
            landUsePage: sessionState.landusePage
        }

        data.filtersVm = filter;
        if (data.filtersVm.primaryFilters) {
            data.filtersVm.primaryFilters.startDate =
                normalizeDate(sessionState.landusePage.startDate);

            data.filtersVm.primaryFilters.endDate =
                normalizeDate(sessionState.landusePage.endDate);
        }
        data.autoRemovedSurveysCountList = sessionState.selectedSitesAndSurveys.filter(f => !f.surveyActive).length;
        data.manuallyRemovedSurveysCount = 0;
        data.selectedSurveys = tripRateSurveyRefs;
        data.exportFiltersVm = exportFiltersVm;
        data.hourly = hourly
        data.extrapolate = extrapolate;
        data.extrapolateValue = extrapolateValueNumeric;
        data.SelectedVehicleType = vehicleType;
        data.startTime = startSlice;
        data.endTime = endSlice;

        const deselected: DeselectedSurveysViewModel[] = [];

        const selected = sessionState.selectedSitesAndSurveys ?? [];

        selected
            .filter(x => !x.siteActive || !x.surveyActive)
            .forEach((item: SurveyViewModel) => {
                const siteRef = item.surveyRef ?? "";
                const surveyRef = item.surveyRef ?? "";
                const reasonSurvey = item.reasonForSurveyRemoval ?? "";
                const reasonSite = item.reasonForSiteRemoval ?? "";

                const reference = siteRef.length > 0 ? siteRef : surveyRef;

                const directSurveyDate = surveyRef.split("|")[1];

                const fallbackSurveyRef =
                    surveys.find(s => s.reference === siteRef)?.surveyRef ?? "";

                const fallbackSurveyDate = fallbackSurveyRef.split("|")[1];

                const surveyDate = directSurveyDate ?? fallbackSurveyDate ?? "";

                const reasonForDeselection =
                    reasonSurvey === "Removed in Site Selection" || reasonSurvey === ""
                        ? reasonSite
                        : reasonSurvey;

                const hasSurveyReason = reasonSurvey.trim().length > 0;
                const hasSiteReason = reasonSite.trim().length > 0;
                const manuallyRemoved = hasSurveyReason || hasSiteReason;

                deselected.push({
                    reference,
                    surveyDate,
                    reasonForDeselection,
                    manuallyRemoved,
                });
            });

        data.exportFiltersVm.deselectedSurveys = deselected;
        const reportTitle = data.exportFiltersVm.reportTitle?.trim();
        postPDF("PDFGenerator", "GetTripRatePDF", reportTitle ? reportTitle : "Trip Rate", false, data);
    }

    const handleChange = () => {
        setExtrapolate(!extrapolate);
    };

    const hourlySwap = () => {
        setHourly(!hourly);
        getData(!hourly);
    };

    const closeDialogs = (refresh?: boolean) => {
        if (refresh) {
            getData();
        }

        setCrossTestOpen(false);
        setPeakHourOpen(false)
        setOpenTripRateGraph(false);
        setPeakChartHourOpen(false);
        setOpenModeChart(false);
        setExportDialog(false);
        setOpenMultiModal(false);
    }

    const handleVehicleChange = (id: number) => () => {
        setVehicleType(id)
    }

    const setSlices = (start, end) => {
        setStartSlice(start);
        setEndSlice(end);
        getData(hourly, start, end);
    }

    const crossTestWarning = <DialogContent>
        <Stack spacing={2}>
            <>
                <DialogContentText style={{ color: 'black', fontWeight: 'bold' }}>
                    What is a Cross Test?
                </DialogContentText>
                <DialogContentText style={{ color: 'black' }}>
                    The TRICS Cross Test procedure compares a mean trip rate figure taken from the trip rate calculation results screen with the corresponding median value taken from a rank order list of trip rates, for the same set of selected data. This automated process is designed to display the percentage variation between the mean and median figures, and is a straightforward process providing an early indication of (but not proving) a dataset's robustness.It can also be used as a handy method of identifying the reliability of 85th and 15th percentile figures as highlighted in a rank order list
                </DialogContentText>
            </>
            <>
                <DialogContentText style={{ color: 'black', fontWeight: 'bold' }}>
                    So there is no set "acceptable" Cross Test variation percentage?Weighting effects and the Cross Test variation percentage
                </DialogContentText>
                <DialogContentText style={{ color: 'black' }}>
                    With larger datasets it would be unusual to see a mean to median variation exceeding 5 percent. With smaller data sets, "weighting" or "bias" in the survey data may have a greater effect. For example one site may be extremely busy whilst the others are not, and this would push the mean trip rates up somewhat. Once you get down to a very small data set (for example 3 surveys), the risk of weighting becomes greater, and this may indeed be reflected in a higher variation percentage figure being displayed. For instance, it would not be unusual for a small dataset of, say, 5 surveys, producing a Cross Test variation greater than 10%. This does not mean that your data is invalid, it just means that "weighting" factors naturally have a stronger effect on the smaller data set. Having said that, should you be presented with a very large variation (say for example 30% or more), then in the interests of good practice and robustness you should carefully examine your site selections to see if all sites selected properly met your intended inclusion criteria. In such cases there may be one particular site that is so different from the rest in the selected set that it is producing a significant "weighting" effect. Where this occurs, a review of the strictness of your search criteria should be undertaken to see if your overall number of selected sites can be increased without compromising your intended criteria. This is a more desired approach than to simply remove the "rogue" site from the selected list. It is important to reiterate that the Cross-Test variation is there to provide users with quick guidance on weighting effects in a selected set, and does not justify the subsequent removal of individual sites from a selected set in order to reduce this level of variation. It should also be noted that should you end up with a very small Cross Test variation, this does not necessarily mean that all of your selections are correct and robust to a given development scenario, it just demonstrates a low level of "weighting" taking place. Care should always be taken when agreeing criteria for site selection.
                </DialogContentText>
            </>
            <>
                <DialogContentText style={{ color: 'black', fontWeight: 'bold' }}>
                    So there is no set "acceptable" Cross Test variation percentage?
                </DialogContentText>
                <DialogContentText style={{ color: 'black' }}>
                    TRICS cannot provide an indication of what is an 'acceptable' mean to median variation percentage for any individual situation. As a general rule, the larger the dataset the smaller the variation will tend to be, but this may not necessarily be the case. The Cross test is intended to provide an indication only of "weighting" effects due to natural fluctuations in the survey data and influencing factors. TRICS encourages users to follow its Good Practice Guidance in selecting a dataset for trip rate calculation, and to achieve a balance between the strictness of selection criteria and the corresponding survey sample used in the trip rate calculation. The general rule is the more sites included the better, but not to the extent that your selection criteria is not selective enough for your particular case. Although the Cross-Test feature is a good indicator of the level of weighting taking place in a dataset, the Good Practice Guide should be TRICS users' first point of reference in producing reliable and robust data outputs. In cases where there are significant levels of Cross Test variation TRICS encourages scrutiny of the selection process and a more inclusive approach to selection criteria where appropriate.
                </DialogContentText>
            </>
            <>
                <DialogContentText style={{ color: 'black', fontWeight: 'bold' }}>
                    So there is no set "acceptable" Cross Test variation percentage?Weighting effects and the Cross Test variation percentageTRICS is designed to generate a range, not an exact prediction
                </DialogContentText>
                <DialogContentText style={{ color: 'black' }}>
                    The thing to remember is that TRICS is designed to provide guidance on a range of potential trip generations, rather than an absolute prediction for any specific scenario. This is because there are many factors that can affect trip rates, both internal and external to the selection parameters available within TRICS. External factors influencing trip generation may include demographics, economics, local competition (for example in the case of retail stores), and a range of other elements. And external influencing factors can have a stronger influence in some scenarios than in others. For example, when viewing a Rank Order Scatterplot, you may not always see a visible correlation between the selected trip rate calculation parameter and the trip generation recorded. This does not mean that there is not a strong relationship between the two, as other factors may be "masking" this relationship by having significant influence."
                </DialogContentText>
            </>
        </Stack>
    </DialogContent>;

    return (
        <LicenceCheck requriedLicence={['Education', 'Single Office (Unlimited Access)', 'Multi-Office', 'Single Office (Single Access)']}>
            {false && params.get('selectedSurvey') !== null &&
                <Grid container size={12}>
                    <Grid size={3}>
                        <Button style={{ marginBottom: '5px' }} fullWidth onClick={() => navigate(`/rankorder?toTab=${fromTab}&selectionType=tripRate`)}>Back To Search</Button>
                    </Grid>
                    <Grid size={9} />
                </Grid>
            }
            <Grid container size={12}>
                <Grid container size={2} component={Paper}>
                    <Grid size={12}>
                        <Stack direction='row' spacing={1} >
                            <Typography variant="body2">Survey Start/End:</Typography>
                            <Typography variant="body2">{sessionState.tripRateAvailableTimeRange}</Typography>
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="body2">
                            All trip rates values are per {tripRateBasis}
                        </Typography>
                        <Typography variant="body2">[Land Use: {sessionState.tripRateMain}] [Sub Use: {sessionState.tripRateSub}]</Typography>
                    </Grid>
                </Grid>
                <Grid container size={4} component={Paper}>
                    <Grid size={12}>
                        <Typography variant="h6">Estimate TRIP rates</Typography>
                    </Grid>
                    <Grid container size={6}>
                        <Grid size={(tripRate !== null && tripRate.containsCovid) ? 10 : 12}>
                            <FormGroup>
                                <FormControlLabel control={<Switch checked={extrapolate} onChange={handleChange} />} label="State TRP Figure & Extrapolate Results" />
                            </FormGroup>
                        </Grid>
                        {tripRate !== null && tripRate.containsCovid &&
                            <Grid size={2}>
                                <AdditionalInfo type="dialog" colour="info" icon="help" dialogTitle="COVID-19 Restrictions"
                                    dialogContent='At least one survey in this selected set was undertaken at a time of Covid-19 (coronavirus) restrictions. Therefore, it is possible that trip levels and patterns at such sites at the time of their surveys may have differed from what would have been considered "normal" pre-pandemic, although to what scale there may be differences (if any) will vary from site to site. Users should note that no surveys were undertaken during full lockdowns (as per the UK lockdown that commenced in March 2020), and the levels of restrictions would have differed at different times and within different regions throughout the pandemic. TRICS cannot provide guidance on whether sites undertaken during restrictions should be included or excluded in sets of data for trip generation. We continued to undertake surveys through the pandemic when full lockdowns did not apply, as it is essential that we continually add surveys to the database moving forwards. So our users are encouraged to apply their own professional judgement in terms of site inclusion, and in the case of site exclusion should always provide their own reasons within their reports. And, as always, we encourage our users to follow the TRICS Good Practice Guide.' />
                            </Grid>}
                    </Grid>
                    {extrapolate && (
                        <Grid size={6}>
                            <TextField label="Estimated TRIP rate value"
                                inputMode={"decimal"}
                                value={extrapolateValue}
                                type="number"
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setExtrapolateValue(newValue === "" ? "" : String(Number.parseFloat(newValue) || 0));
                                }}
                                variant="outlined"
                                slotProps={{ input: { endAdornment: <InputAdornment position="end">{tripRate?.unit?.includes("*") ? `Per ${tripRate?.calculationValue} ` + tripRate?.abbreviation : `Per ${tripRate?.calculationValue} ` + tripRate?.unit}</InputAdornment> } }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid container size={3} component={Paper}>
                    <SurveyPeriod calculate={setSlices} tripRate={tripRate} />
                </Grid>
                <Grid container size={3} component={Paper}>
                    <PDFLink container="GeneralDocs" label="Descriptive Note on TRICS for Data Recipients" file="What is TRICS" />
                    <PDFLink container="GeneralDocs" label="Note on TRICS Calculation Roundings" file="Rounding" />
                </Grid>
                <Grid container size={3}>
                    <Grid container size={12} component={Paper}>
                        <Grid size={11}>
                            <Button onClick={() => setCrossTestOpen(true)}>Cross Test</Button>
                        </Grid>
                        <Grid size={1}>
                            <WarningPopup
                                title="Good Practice Guidance Advice - Note on cross-test facility"
                                content={crossTestWarning}
                                type="error"
                            />
                        </Grid>
                        <Button onClick={() => setPeakHourOpen(true)}>Key Period Trip Rates</Button>
                        <Button onClick={() => setOpenTripRateGraph(true)}>Trip Rate Graph</Button>
                        <Button onClick={() => { getLargeChartData(); setOpenModeChart(true); }}>Trip Rate Graph by mode</Button>
                        <Button onClick={openSiteTripRateGraph}>Trip Rate Line Graph</Button>
                        <Button onClick={() => setPeakChartHourOpen(true)}>Peak Hour Analysis</Button>
                        {/*TODO: Remove this button*/}
                        {/*<Button onClick={() => exportTripRateChartPdf()}>Trip Rate Sites Graph PDF</Button>*/}
                        <Button onClick={() => setExportDialog(true)}>Export</Button>
                        {sessionState.landusePage.isMultiModal &&
                            <Button onClick={() => setOpenMultiModal(true)}>Modal Split Pie Chart</Button>
                        }

                        {sessionState.tripRateMain == '02' &&
                            <Button onClick={hourlySwap}>Switch to {hourly ? 'Half Hourly' : 'Hourly'}</Button>
                        }
                    </Grid>
                    <Grid container size={12} component={Paper}>
                        <Grid size={12}>
                            <Typography variant="h6">Trip Rate Mode</Typography>
                        </Grid>
                        <Line />
                        <Grid size={12}>
                            <MenuList>
                                {modes.map((mode: ModeSelectionViewModel) =>
                                    <MenuItem key={mode.modeId} value={mode.modeId} onClick={handleVehicleChange(mode.modeId)} disabled={!mode.isSelectable} selected={vehicleType == mode.modeId}>
                                        {mode.isSelectable ?
                                            <Avatar
                                                variant="rounded"
                                                sx={{ width: 30, height: 30, marginRight: '8px', bgcolor: vehicleType == mode.modeId ? theme.palette.primary.main : "gray" }}
                                                src={`/Icons/${mode.modeIcon}`} /> :
                                            <Avatar
                                                variant="rounded"
                                                sx={{ width: 30, height: 30, marginRight: '8px', bgcolor: "gray" }}
                                                src={`/Icons/${mode.modeIcon}`} />
                                        }
                                        <Typography variant="caption">{mode.modeName}</Typography>
                                        <Box flexGrow={1} />
                                        {mode.isSelectable ?
                                            <Typography variant="caption">{`${mode.modeCount} Surveys`}</Typography>
                                            :
                                            <Typography variant="caption">{mode.notes}</Typography>

                                        }
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Grid>
                        <Grid size={12}>
                            <PDFLink label="Click Here for rules on calculating trips by count type" container="GeneralDocs" file="Calculation by Mode" />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container size={9} sx={{ minWidth: 0 }}>
                    {tripRate != null &&
                        <TripRateTable tripRateCalculation={tripRate} loading={loading} extrapolate={extrapolate} extrapolateValue={extrapolateValueNumeric} timeSliceStart={startSlice} timeSliceEnd={endSlice} />
                    }
                </Grid>
                {tripRate != null && <>
                    {openModeChart && <FarTooBigChart surveys={chart} onClose={closeDialogs} open={openModeChart} />}
                    {openTripRateGraph && <TripRateTotalChart vm={tripRate} open={openTripRateGraph} onClose={closeDialogs} />}
                    {crossTestOpen && <CrossTestDialog dialogOpen={crossTestOpen} handleClose={closeDialogs} tripRate={tripRate} vehicleType={vehicleType} hourly={hourly} surveyRefs={tripRateSurveyRefs} />}
                    {peakHourOpen && <KeyPeriodDialog dialogOpen={peakHourOpen} handleClose={closeDialogs} tripRate={tripRate} />}
                    {peakHourChartOpen && <PeakHourChart onClose={closeDialogs} open={peakHourChartOpen} vehicleType={vehicleType} surveyRefs={tripRateSurveyRefs} />}
                    {exportDialog && <ExportDialog handleClose={closeDialogs} dialogOpen={exportDialog} exportTripRate={getReport} availableModes={modes} selectedModeId={vehicleType} />}
                    {openMultiModal && <ModalTypeChart onClose={closeDialogs} open={openMultiModal} tripRate={tripRate} surveyRefs={tripRateSurveyRefs} headerIndex={effectiveHeaderIndex} hourly={hourly} />}

                </>
                }
                <TripRateSitesGraphDialog
                    open={siteGraphOpen}
                    onClose={closeSiteTripRateGraph}
                    origin="tripRateCalculation"
                    returnLabel="Back to Graph"
                    returnTo={`${location.pathname}${location.search}`}
                    selectedMode={siteGraphMode}
                    direction={siteGraphDirection}
                    view={siteGraphView}
                    allowModeSelection={true}
                    availableModes={modes}
                    onSelectedModeChange={setSiteGraphMode}
                    onDirectionChange={setSiteGraphDirection}
                    onViewChange={setSiteGraphView}
                />
            </Grid>

        </LicenceCheck>
    )
}
